// WorkspaceDetail - workspace detail and collaboration page
// Implements US-2.1: Create and List Workspaces (basic view)
// Implements US-2.2: Join Workspace with Role Assignment (role badge display)
// Implements US-2.3: Rename Workspace
// Implements US-2.4: Close Workspace with Export Status Check
// Implements US-3.1: Item CRUD and Drag-Drop Reordering
// Implements US-3.2: RICE Scoring Interface and Calculation
// Implements US-4.1: WebSocket Infrastructure and Presence System
// Implements US-4.2: Real-time Synchronization of Workspace Changes
// Implements US-4.3: Collaborative Notes Panel
// Implements US-5.1: RICE Ranked List and Bar Chart
// Implements US-5.2: 2x2 Matrix Visualizations (Value/Effort, Eisenhower)
// Implements US-5.3: MoSCoW Grouped Lists with Color Coding
// Implements US-5.4: Risk Assessment Matrix with Color Zones

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Edit2, Check, X, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { PresencePanel } from '../components/PresencePanel';
import { ToastContainer, ToastData } from '../components/ToastContainer';
import { ItemList } from '../components/ItemList';
import { AddItemInput } from '../components/AddItemInput';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { NotesPanel } from '../components/NotesPanel';
import { ExportModal } from '../components/ExportModal';
import { CloseWorkspaceModal } from '../components/CloseWorkspaceModal';
import { RICEVisualization } from '../components/visualizations/RICEVisualization';
import { MatrixVisualization } from '../components/visualizations/MatrixVisualization';
import { CategoricalMatrixVisualization } from '../components/visualizations/CategoricalMatrixVisualization';
import { MoSCoWVisualization } from '../components/visualizations/MoSCoWVisualization';
import { RiskAssessmentVisualization } from '../components/visualizations/RiskAssessmentVisualization';
import styles from './WorkspaceDetail.module.css';
import { getWorkspaceById, renameWorkspace, closeWorkspace } from '../services/workspaceService';
import { createItem, updateItem, deleteItem } from '../services/itemService';
import { exportWorkspace } from '../services/exportService';
import {
  WorkspaceDetail as WorkspaceDetailType,
  Score,
  Item,
  Criterion,
} from '../types';
import {
  ParticipantPresence,
  ParticipantJoinedEvent,
  ParticipantLeftEvent,
  ItemCreatedEvent,
  ItemUpdatedEvent,
  ItemDeletedEvent,
  ScoresUpdatedEvent,
  WorkspaceUpdatedEvent,
  WorkspaceClosedEvent,
  CalculationResult,
} from '../types/socket';
import { useSocket } from '../hooks/useSocket';

/**
 * RENDER workspace detail page
 * - Shows workspace information
 * - Placeholder for future functionality (items, scoring, notes)
 */
const WorkspaceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // STATE management
  const [workspace, setWorkspace] = useState<WorkspaceDetailType | null>(null);
  const [role, setRole] = useState<'user' | 'viewer' | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // PRESENCE state
  const [presence, setPresence] = useState<ParticipantPresence[]>([]);
  const [recentJoin, setRecentJoin] = useState<ParticipantJoinedEvent | null>(null);
  const [recentLeave, setRecentLeave] = useState<ParticipantLeftEvent | null>(null);


  // DELETE confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    itemId: string | null;
    itemTitle: string;
  }>({
    isOpen: false,
    itemId: null,
    itemTitle: '',
  });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // EXPORT modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  // CLOSE modal state
  const [isCloseModalOpen, setIsCloseModalOpen] = useState<boolean>(false);

  // RENAME state
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>('');
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState<boolean>(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  /**
   * HANDLE real-time presence events via WebSocket
   */
  const handlePresenceUpdated = useCallback((newPresence: ParticipantPresence[]) => {
    setPresence(newPresence);
  }, []);

  const handleParticipantJoined = useCallback((event: ParticipantJoinedEvent) => {
    setRecentJoin(event);
  }, []);

  const handleParticipantLeft = useCallback((event: ParticipantLeftEvent) => {
    setRecentLeave(event);
  }, []);

  /**
   * HANDLE real-time events via WebSocket
   * NOTE: These are the ONLY places where item state is updated
   */
  const handleItemCreated = useCallback((event: ItemCreatedEvent) => {
    console.log('Item created event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      // SAFETY CHECK: Verify item doesn't already exist
      const exists = prev.items.some((item) => item.id === event.item.id);
      if (exists) {
        console.log('Item already exists, skipping duplicate');
        return prev;
      }
      return {
        ...prev,
        items: [...prev.items, event.item],
      };
    });
  }, []);

  const handleItemUpdated = useCallback((event: ItemUpdatedEvent) => {
    console.log('Item updated event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      // UPDATE item and sort by order field
      const updatedItems = prev.items.map((item) =>
        item.id === event.item.id ? event.item : item
      );
      // SORT by order field to reflect drag-and-drop changes
      // NOTE: Handle null order values (put them at the end)
      updatedItems.sort((a, b) => {
        if (a.order === null && b.order === null) return 0;
        if (a.order === null) return 1;
        if (b.order === null) return -1;
        return a.order - b.order;
      });
      return {
        ...prev,
        items: updatedItems,
      };
    });
  }, []);

  const handleItemDeleted = useCallback((event: ItemDeletedEvent) => {
    console.log('Item deleted event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.filter((item) => item.id !== event.itemId),
      };
    });
  }, []);

  const handleWorkspaceUpdated = useCallback((event: WorkspaceUpdatedEvent) => {
    console.log('Workspace updated event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      return { ...prev, name: event.name };
    });
  }, []);

  const handleWorkspaceClosed = useCallback((event: WorkspaceClosedEvent) => {
    console.log('Workspace closed event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      return { ...prev, status: 'closed' as const };
    });
    const toastId = `closed-${Date.now()}`;
    setToasts((prev) => [
      ...prev,
      {
        id: toastId,
        message: 'This workspace has been closed',
        type: 'warning',
      },
    ]);
  }, []);

  /**
   * HANDLE score updates via WebSocket
   * NOTE: Updates item scores and state when another user scores an item
   */
  const handleScoresUpdated = useCallback((event: ScoresUpdatedEvent) => {
    console.log('Scores updated event:', event);
    setWorkspace((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) =>
          item.id === event.itemId
            ? {
                ...item,
                scores: event.scores,
                state: event.state || item.state,
              }
            : item
        ),
      };
    });
  }, []);

  // SOCKET and presence management with event handlers
  const { isConnected, socket, join } = useSocket({
    handlers: {
      onPresenceUpdated: handlePresenceUpdated,
      onParticipantJoined: handleParticipantJoined,
      onParticipantLeft: handleParticipantLeft,
      onItemCreated: handleItemCreated,
      onItemUpdated: handleItemUpdated,
      onItemDeleted: handleItemDeleted,
      onScoresUpdated: handleScoresUpdated,
      onWorkspaceUpdated: handleWorkspaceUpdated,
      onWorkspaceClosed: handleWorkspaceClosed,
    },
  });
  const [toasts, setToasts] = useState<ToastData[]>([]);

  /**
   * FETCH workspace details from API
   */
  const fetchWorkspace = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await getWorkspaceById(id);

      if (response.success) {
        setWorkspace(response.workspace);
        setRole(response.role);
      } else {
        throw new Error(response.error || 'Workspace konnte nicht geladen werden');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein unerwarteter Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // GET auth state to wait for authentication before fetching
  const { loading: authLoading } = useAuth();

  /**
   * FETCH workspace on mount
   * WAIT for authentication to complete before fetching to prevent race condition
   */
  useEffect(() => {
    if (!id) {
      setError('Workspace-ID ist erforderlich');
      setLoading(false);
      return;
    }

    // SKIP fetching if auth is still loading
    if (authLoading) {
      return;
    }

    fetchWorkspace();
  }, [id, fetchWorkspace, authLoading]);

  /**
   * JOIN workspace via WebSocket when workspace is loaded
   * NOTE: Only join once when ID is available, not on every workspace state change
   * IDEMPOTENCY: Backend handles duplicate joins gracefully (Defense in Depth approach)
   * React Strict Mode will call this twice, but backend deduplicates and resends presence
   */
  useEffect(() => {
    if (!id) {
      return;
    }

    // JOIN workspace (backend handles idempotency)
    join(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only re-join if workspace ID changes, not on state updates

  /**
   * HANDLE participant joined notifications
   */
  useEffect(() => {
    if (recentJoin) {
      const toastId = `join-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: `${recentJoin.name} ist beigetreten (${recentJoin.role === 'user' ? 'Ersteller:in' : 'Betrachter:in'})`,
          type: 'info',
        },
      ]);
      setRecentJoin(null); // Clear after showing toast
    }
  }, [recentJoin]);

  /**
   * HANDLE participant left notifications
   */
  useEffect(() => {
    if (recentLeave) {
      const toastId = `leave-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: `${recentLeave.name} hat die Session verlassen`,
          type: 'info',
        },
      ]);
      setRecentLeave(null); // Clear after showing toast
    }
  }, [recentLeave]);

  /**
   * REMOVE toast by ID
   */
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * HANDLE back to workspaces button
   */
  const handleBack = () => {
    navigate('/workspaces');
  };

  /**
   * START editing workspace name
   * - Sets editing mode and initializes edited name
   * - Focuses input field after state update
   */
  const startEditingName = () => {
    if (!workspace || role !== 'user' || workspace.status !== 'active') return;
    setEditedName(workspace.name);
    setIsEditingName(true);
  };

  /**
   * FOCUS input when editing starts
   * NOTE: useEffect needed because state update doesn't happen immediately
   */
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  /**
   * CANCEL editing workspace name
   * - Resets editing state without saving
   */
  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  /**
   * SAVE workspace name
   * - Validates name is not empty
   * - Calls API to rename workspace
   * - Workspace state updates via WebSocket broadcast
   */
  const saveWorkspaceName = async () => {
    if (!workspace || !id) return;

    // VALIDATE name is not empty
    const trimmedName = editedName.trim();
    if (!trimmedName) {
      const toastId = `error-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: 'Workspace-Name darf nicht leer sein',
          type: 'error',
        },
      ]);
      return;
    }

    // VALIDATE name length
    if (trimmedName.length > 200) {
      const toastId = `error-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: 'Workspace-Name darf maximal 200 Zeichen lang sein',
          type: 'error',
        },
      ]);
      return;
    }

    // SKIP if name hasn't changed
    if (trimmedName === workspace.name) {
      setIsEditingName(false);
      return;
    }

    try {
      setIsRenamingWorkspace(true);
      const response = await renameWorkspace(id, { name: trimmedName });

      if (response.success) {
        // NOTE: Workspace state will be updated via WebSocket broadcast (handleWorkspaceUpdated)
        // This ensures all participants see the change in real-time
        setIsEditingName(false);
        setEditedName('');
      } else {
        throw new Error(response.error || 'Workspace konnte nicht umbenannt werden');
      }
    } catch (error) {
      console.error('Failed to rename workspace:', error);
      const toastId = `error-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: error instanceof Error ? error.message : 'Workspace konnte nicht umbenannt werden',
          type: 'error',
        },
      ]);
    } finally {
      setIsRenamingWorkspace(false);
    }
  };

  /**
   * HANDLE keyboard events for name editing
   * - Enter: Save
   * - Escape: Cancel
   */
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveWorkspaceName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditingName();
    }
  };

  /**
   * HANDLE open close workspace modal
   */
  const handleOpenCloseModal = () => {
    setIsCloseModalOpen(true);
  };

  /**
   * HANDLE cancel close workspace
   */
  const handleCancelClose = () => {
    setIsCloseModalOpen(false);
  };

  /**
   * HANDLE confirm close workspace
   * - Calls API to close workspace
   * - Workspace state updates via WebSocket broadcast
   */
  const handleConfirmClose = async () => {
    if (!workspace || !id) return;

    try {
      const response = await closeWorkspace(id);

      if (response.success) {
        // NOTE: Workspace status will be updated via WebSocket broadcast (handleWorkspaceClosed)
        // This ensures all participants are notified
        // Toast is shown by handleWorkspaceClosed, not here, to avoid duplicate toasts
        setIsCloseModalOpen(false);
      } else {
        throw new Error(response.error || 'Workspace konnte nicht geschlossen werden');
      }
    } catch (error) {
      console.error('Failed to close workspace:', error);
      throw error; // Re-throw to let modal handle the error
    }
  };

  /**
   * HANDLE export workspace
   * - Opens export modal when export button is clicked
   */
  const handleOpenExport = () => {
    setIsExportModalOpen(true);
  };

  /**
   * HANDLE export modal close
   */
  const handleCloseExport = () => {
    setIsExportModalOpen(false);
  };

  /**
   * HANDLE export action
   * - Calls export API
   * - Downloads file automatically via presigned URL
   * - Shows success toast
   */
  const handleExport = async (format: 'pdf' | 'png' | 'csv' | 'json') => {
    try {
      const response = await exportWorkspace(id!, format);

      // DOWNLOAD file automatically by opening the presigned URL
      window.open(response.downloadUrl, '_blank');

      // SHOW success toast
      const toastId = `export-${Date.now()}`;
      setToasts((prev) => [
        ...prev,
        {
          id: toastId,
          message: `Workspace erfolgreich als ${format.toUpperCase()} exportiert`,
          type: 'success',
        },
      ]);

      // CLOSE modal after success
      setIsExportModalOpen(false);
    } catch (error) {
      // ERROR will be shown in modal by the modal component
      throw error;
    }
  };

  /**
   * HANDLE add new item
   * NOTE: No optimistic update - wait for WebSocket broadcast to update UI
   * This prevents duplicate items from race condition between optimistic update and broadcast
   */
  const handleAddItem = async (title: string) => {
    if (!workspace || !id) return;

    try {
      const response = await createItem(id, title);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create item');
      }
      // Item will be added via WebSocket broadcast (handleItemCreated)
    } catch (err) {
      console.error('Failed to add item:', err);
      throw err;
    }
  };

  /**
   * HANDLE edit item
   * NOTE: No optimistic update - wait for WebSocket broadcast to update UI
   */
  const handleEditItem = async (itemId: string, newTitle: string) => {
    if (!workspace || !id) return;

    try {
      const response = await updateItem(id, itemId, { title: newTitle });
      if (!response.success) {
        throw new Error(response.error || 'Failed to update item');
      }
      // Item will be updated via WebSocket broadcast (handleItemUpdated)
    } catch (err) {
      console.error('Failed to edit item:', err);
      throw err;
    }
  };

  /**
   * OPEN delete confirmation dialog
   */
  const openDeleteDialog = (itemId: string) => {
    const item = workspace?.items.find((i) => i.id === itemId);
    if (!item) return;

    setDeleteDialog({
      isOpen: true,
      itemId: item.id,
      itemTitle: item.title,
    });
  };

  /**
   * CLOSE delete confirmation dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      isOpen: false,
      itemId: null,
      itemTitle: '',
    });
    setIsDeleting(false);
  };

  /**
   * CONFIRM delete item
   * NOTE: No optimistic update - wait for WebSocket broadcast to update UI
   */
  const confirmDeleteItem = async () => {
    if (!deleteDialog.itemId || !workspace || !id) return;

    try {
      setIsDeleting(true);
      const response = await deleteItem(id, deleteDialog.itemId);
      if (response.success) {
        // Item will be removed via WebSocket broadcast (handleItemDeleted)
        closeDeleteDialog();
      } else {
        throw new Error(response.error || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
      setIsDeleting(false);
    }
  };

  /**
   * HANDLE local score updates
   * NOTE: Actual state update happens via WebSocket broadcast
   * This callback is mainly for logging/feedback
   */
  const handleLocalScoresUpdated = useCallback(
    (itemId: string, scores: Score[], result: CalculationResult) => {
      console.log('Scores saved locally:', { itemId, scores, result });
      // No state update needed - WebSocket broadcast will handle it
    },
    []
  );

  /**
   * HANDLE item reorder (drag-and-drop)
   * Updates order field for all items after reordering
   * NOTE: Updates happen via WebSocket broadcast to all participants
   */
  const handleReorderItems = async (reorderedItems: Item[]) => {
    if (!workspace || !id) return;

    try {
      // UPDATE each item's order in the database
      // Note: We could optimize this by only updating changed orders,
      // but for simplicity we update all items
      for (const item of reorderedItems) {
        // SKIP items with null order (shouldn't happen in reorder, but handle it)
        if (item.order !== null) {
          await updateItem(id, item.id, { order: item.order });
        }
      }
      // Items will be reordered via WebSocket broadcast (handleItemUpdated)
    } catch (err) {
      console.error('Failed to reorder items:', err);
    }
  };

  /**
   * FORMAT date string to readable format
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // RENDER loading state
  if (loading) {
    return (
      <Layout>
        <div className={styles.loadingState}>
          <p>Lade Workspace...</p>
        </div>
      </Layout>
    );
  }

  // RENDER error state
  if (error || !workspace) {
    return (
      <Layout>
        <div className={styles.errorState}>
          <h2>Fehler</h2>
          <p>{error || 'Workspace nicht gefunden'}</p>
          <button className={styles.backButton} onClick={handleBack}>
            Zurück zu Workspaces
          </button>
        </div>
      </Layout>
    );
  }

  /**
   * RENDER header content for Layout
   */
  const header = (
    <>
      <div className={styles.headerLeft}>
        <div className={styles.headerTop}>
          {isEditingName ? (
            // EDIT MODE: Show input field with save/cancel buttons
            <div className={styles.nameEditContainer}>
              <input
                ref={nameInputRef}
                type="text"
                className={styles.nameInput}
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleNameKeyDown}
                maxLength={200}
                disabled={isRenamingWorkspace}
              />
              <div className={styles.nameEditActions}>
                <button
                  className={styles.nameEditButton}
                  onClick={saveWorkspaceName}
                  disabled={isRenamingWorkspace}
                  title="Speichern (Enter)"
                >
                  <Check size={18} strokeWidth={2} />
                </button>
                <button
                  className={styles.nameEditButton}
                  onClick={cancelEditingName}
                  disabled={isRenamingWorkspace}
                  title="Abbrechen (Esc)"
                >
                  <X size={18} strokeWidth={2} />
                </button>
              </div>
            </div>
          ) : (
            // VIEW MODE: Show workspace name with edit button (for owners only)
            <div className={styles.nameViewContainer}>
              <h1 className={styles.title}>{workspace.name}</h1>
              {role === 'user' && workspace.status === 'active' && (
                <button
                  className={styles.editNameButton}
                  onClick={startEditingName}
                  title="Workspace umbenennen"
                >
                  <Edit2 size={18} strokeWidth={2} />
                </button>
              )}
            </div>
          )}
          <span className={`${styles.roleBadge} ${styles[role || '']}`}>
            {role === 'user' ? 'Ersteller:in' : 'Betrachter:in'}
          </span>
        </div>
        <div className={styles.metadata}>
          <span className={styles.metadataItem}>
            <strong>Framework:</strong> {workspace.framework.name}
          </span>
          <span className={styles.metadataItem}>
            <strong>Raum Code:</strong>{' '}
            <span className={styles.roomCode}>{workspace.roomCode}</span>
          </span>
          <span className={styles.metadataItem}>
            <strong>Erstellt:</strong> {formatDate(workspace.createdAt)}
          </span>
        </div>
      </div>
      <div className={styles.headerActions}>
        {role === 'user' && workspace.status === 'active' && (
          <>
            <button className={styles.closeButton} onClick={handleOpenCloseModal}>
              <XCircle size={18} strokeWidth={2} />
              <span>Workspace schließen</span>
            </button>
            <button className={styles.exportButton} onClick={handleOpenExport}>
              <Download size={18} strokeWidth={2} />
              <span>Exportieren</span>
            </button>
          </>
        )}
        <button className={styles.backButton} onClick={handleBack}>
          ← Zurück zu Workspaces
        </button>
      </div>
    </>
  );

  const isReadOnly = role === 'viewer' || workspace.status === 'closed';

  return (
    <Layout header={header}>
      {/* ITEM MANAGEMENT SECTION */}
      <div className={styles.itemManagementSection}>
        <h3>Themen / Elemente</h3>

        {/* SOFT limit warning - show when approaching 20 items */}
        {workspace.items.length >= 15 && workspace.items.length < 20 && (
          <div className={styles.softLimitWarning}>
            <span className={styles.warningIcon}>⚠</span>
            <span>
              Du hast {workspace.items.length} Elemente. Halte die Anzahl idealerweise unter 20 für beste Ergebnisse.
            </span>
          </div>
        )}
        {workspace.items.length >= 20 && (
          <div className={styles.softLimitWarning}>
            <span className={styles.warningIcon}>⚠</span>
            <span>
              Du hast {workspace.items.length} Elemente. Für optimale Priorisierung solltest du einige Elemente entfernen oder zusammenfassen.
            </span>
          </div>
        )}

        {/* ADD item input - only for users in active workspaces */}
        {!isReadOnly && (
          <AddItemInput
            onAddItem={handleAddItem}
            disabled={workspace.status === 'closed'}
          />
        )}

        {/* ITEM list */}
        <ItemList
          items={workspace.items}
          framework={workspace.framework}
          isReadOnly={isReadOnly}
          onEditItem={handleEditItem}
          onDeleteItem={openDeleteDialog}
          onReorderItems={handleReorderItems}
          onScoresUpdated={handleLocalScoresUpdated}
        />
      </div>

      {/* VISUALIZATION SECTION - Show framework-specific visualizations */}
      {workspace.items.length > 0 && (
        <div className={styles.visualizationSection}>
          <h3>Ergebnisse</h3>
          {workspace.framework.slug === 'rice' && (
            <RICEVisualization items={workspace.items} />
          )}
          {workspace.framework.slug === 'value-effort' && (
            <MatrixVisualization items={workspace.items} framework={workspace.framework} />
          )}
          {workspace.framework.slug === 'eisenhower' && (
            <CategoricalMatrixVisualization items={workspace.items} framework={workspace.framework} />
          )}
          {workspace.framework.slug === 'moscow' && (
            <MoSCoWVisualization items={workspace.items} />
          )}
          {workspace.framework.slug === 'risk-assessment' && (
            <RiskAssessmentVisualization items={workspace.items} />
          )}
        </div>
      )}

      {/* FRAMEWORK details */}
      <div className={styles.infoSection}>
        <h3>Framework: {workspace.framework.name}</h3>
        {workspace.framework.description && (
          <p className={styles.frameworkDescription}>{workspace.framework.description}</p>
        )}
        <p>
          <strong>Typ:</strong>{' '}
          {workspace.framework.calculationType.charAt(0).toUpperCase() +
            workspace.framework.calculationType.slice(1)}
        </p>
        <p>
          <strong>Kriterien:</strong>{' '}
          {workspace.framework.criteria?.criteriaList
            ? workspace.framework.criteria.criteriaList.map((c: Criterion) => c.name).join(', ')
            : 'N/A'}
        </p>
      </div>

      {/* BASIC workspace info */}
      <div className={styles.infoSection}>
        <h3>Workspace-Informationen</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <strong>Status</strong>
            <span className={`${styles.statusBadge} ${styles[workspace.status]}`}>
              {workspace.status}
            </span>
          </div>
          <div className={styles.infoCard}>
            <strong>Elemente</strong>
            <span>{workspace.items.length}</span>
          </div>
          <div className={styles.infoCard}>
            <strong>Notizen</strong>
            <span>{workspace.notes.length}</span>
          </div>
          <div className={styles.infoCard}>
            <strong>Exportiert</strong>
            <span>{workspace.exported ? 'Ja' : 'Nein'}</span>
          </div>
        </div>
      </div>

      {/* NOTES PANEL - Collaborative notes section */}
      <div className={styles.notesSection}>
        <NotesPanel workspaceId={id!} socket={socket} />
      </div>

      {/* PRESENCE PANEL - Collapsible bottom panel showing participants */}
      <PresencePanel presence={presence} isConnected={isConnected} />

      {/* DELETE CONFIRMATION DIALOG */}
      <DeleteConfirmDialog
        isOpen={deleteDialog.isOpen}
        itemTitle={deleteDialog.itemTitle}
        onConfirm={confirmDeleteItem}
        onCancel={closeDeleteDialog}
        isDeleting={isDeleting}
      />

      {/* EXPORT MODAL */}
      <ExportModal
        isOpen={isExportModalOpen}
        workspaceName={workspace.name}
        onExport={handleExport}
        onClose={handleCloseExport}
      />

      {/* CLOSE WORKSPACE MODAL */}
      <CloseWorkspaceModal
        isOpen={isCloseModalOpen}
        workspaceName={workspace.name}
        hasBeenExported={workspace.exported}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />

      {/* TOAST NOTIFICATIONS - Join/leave notifications */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </Layout>
  );
};

export default WorkspaceDetail;
